<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./CareerDetail.translations.json";
	import type { CareerDetailProps } from "./CareerDetailProps.js";
	import Button from "../components/Button.svelte";
	import CareerFormModal from "../components/Career/CareerFormModal.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { formatDateTime } from "../../../core/utils/formatDateTime.js";
	import Content from "../components/Content.svelte";
	import Breadcrumbs from "../components/Breadcrumbs.svelte";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import CareerDesktopGallery from "../components/Career/CareerDesktopGallery.svelte";
	import CareerMobileGallery from "../components/Career/CareerMobileGallery.svelte";
	import { getTenant } from "../getTenant.js";
	import { getContext } from "svelte";
	import type { GlobalTemplateProps } from "../GlobalTemplateProps.js";
	import { pageSlugContextKey } from "../contexts/pageSlugContextKey.js";

	type $$Props = CareerDetailProps;

	const { position, breadcrumbs } = $$restProps as $$Props;
	const tenant = getTenant();
	const pageSlug = getContext<GlobalTemplateProps["pageSlug"]>(pageSlugContextKey);

	const translate = getTranslate(translations);

	let showCareerModal = false;

	function openCareerModal(): void {
		showCareerModal = true;
	}
</script>

<CareerFormModal bind:show={showCareerModal} {position} />

<Layout>
	<div class="container">
		<div class="mx-4 md:mx-16 xl:mx-32">
			<section class="text-ltprimary !mb-16 lg:!mb-24">
				<div
					class="hidden items-center gap-4 lining-nums tabular-nums md:flex [&_nav]:!m-0 [&_nav]:!p-0 [&_ul]:!flex-wrap [&_ul]:!overflow-visible [&_ul_a]:!whitespace-normal [&_ul_a]:!text-base/none [&_ul_a]:!font-light [&_ul_a]:!tracking-normal [&_ul_li]:!text-base/none"
				>
					<span class="bg-ltprimary-200 h-px w-8 shrink-0"></span>
					<Breadcrumbs pageSlug={pageSlug[tenant.locale] ?? ""} {breadcrumbs} showBackLink={false} />
				</div>

				<div class="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16 xl:gap-24">
					<div>
						<h1 class="text-ltprimary p-0 font-light uppercase tracking-wider">
							{nbspify(position.name)}
						</h1>

						{#if position.labels.length > 0}
							<div class="mt-6 flex flex-wrap items-center gap-2 md:gap-3">
								{#each position.labels as label (label)}
									<span
										class="bg-lttertiary-100 text-ltprimary-300 font-bahnschrift !text-2xs px-[0.313rem] py-1 font-normal uppercase leading-none tracking-wider"
									>
										{nbspify(label)}
									</span>
								{/each}
							</div>
						{/if}

						<dl class="font-lexend mt-8 flex flex-col gap-2 text-base/[1.6] font-bold tracking-normal lg:mt-10">
							{#if position.startDate}
								<div class="flex flex-wrap gap-x-2">
									<dt class="text-ltprimary-300">{translate("startDate")}:</dt>
									<dd class="text-ltsecondary-300">{nbspify(position.startDate)}</dd>
								</div>
							{/if}
							<div class="flex flex-wrap gap-x-2">
								<dt class="text-ltprimary-300">{translate("publishedAt")}:</dt>
								<dd class="text-ltsecondary-300">{formatDateTime(position.createdAt, tenant.locale)}</dd>
							</div>
						</dl>

						<Button
							class="mt-8 text-center !text-base/none !tracking-wider lg:mt-10"
							on:click={openCareerModal}
							variant="primary"
							icon={arrowRight}
						>
							{translate("applyForPosition")}
						</Button>
					</div>

					<div class="text-ltsecondary leading-normal tracking-[0.02em]">
						<h2 class="text-ltprimary m-0 p-0 font-light uppercase">
							{translate("aboutPosition")}
						</h2>

						<p class="mt-6 lg:!text-base">{nbspify(position.perex)}</p>

						<div
							class="[&_.ckeditor-content_h2]:text-ltprimary [&_.ckeditor-content_h2]:!text-md [&_.ckeditor-content_h2]:lg:!text-2md [&_.ckeditor-content_h3]:text-ltprimary [&_.ckeditor-content_h3]:lg:!text-md [&_.ckeditor-content_h2]:!mt-10 [&_.ckeditor-content_h2]:font-normal [&_.ckeditor-content_h2]:uppercase [&_.ckeditor-content_h2]:tracking-wider [&_.ckeditor-content_h3]:!mt-8 [&_.ckeditor-content_h3]:!text-base [&_.ckeditor-content_h3]:font-normal [&_.ckeditor-content_h3]:uppercase [&_.ckeditor-content_h3]:tracking-wider"
						>
							<Content content={position.content} />
						</div>
					</div>
				</div>
			</section>
		</div>
	</div>

	{#if position.images.length > 0}
		<div class="container">
			<section class="hidden md:block">
				<div class="mx-4 md:mx-16 xl:mx-32">
					<CareerDesktopGallery {position} images={position.images} />
				</div>
			</section>
		</div>
		<section class="md:hidden">
			<CareerMobileGallery class="md:hidden" {position} images={position.images} />
		</section>
	{/if}

	<section class="bg-lttertiary-300 !mb-0">
		<div class="mx-auto max-w-[120rem]">
			<div class="mx-4 grid grid-cols-1 items-center gap-8 py-12 md:mx-16 lg:grid-cols-2 lg:gap-16 lg:py-16 xl:mx-32">
				<h2 class="text-ltprimary m-0 p-0 font-light uppercase tracking-wider">
					{translate("joinUsTitle")}
				</h2>
				<div>
					<p class="text-ltsecondary-300 !mb-0 text-xs/[1.6] tracking-[0.02em]">
						{translate("joinUsDesc")}
					</p>
					<Button
						class="mt-8 text-center !text-base/none !tracking-wider"
						on:click={openCareerModal}
						variant="primary"
						icon={arrowRight}
					>
						{translate("applyForPosition")}
					</Button>
				</div>
			</div>
		</div>
	</section>
</Layout>
